import { ClickEvent, ClickListener, DomEvent, LabelProps, TreeNode, TreeNodeProps } from "@mcleod/components";
import { ArrayUtil } from "@mcleod/core";
import { MenuTreeNode } from "../menu/MenuTreeNode";
import { DatabaseMenuItem } from "./DatabaseMenuItem";
import { MenuEditor } from "./MenuEditor";
import { MenuEditorTree } from "./MenuEditorTree";
import { MenuEditorUtil } from "./MenuEditorUtil";

export interface MenuEditorTreeNodeProps extends TreeNodeProps {
    menuItem: DatabaseMenuItem;
    menuEditor: MenuEditor;
}

export class MenuEditorTreeNode extends MenuTreeNode {
    public children: MenuEditorTreeNode[];
    public menuItem: DatabaseMenuItem;
    public menuEditor: MenuEditor;
    tree: MenuEditorTree;

    private imageClickListener: ClickListener = (event: ClickEvent) => {
        this.expanded = !this.expanded;
        event.stopPropagation();
    }

    constructor(props: Partial<MenuEditorTreeNodeProps>) {
        super({
            themeKey: !ArrayUtil.isEmptyArray(props.menuItem.items) ? "menu.branchNode": null,
            expandOnClick: false,
            fontSize: "medium",
            ...props
        });

        this.syncUI();
        this.addChildrenFromMenuItem();

        if (this.menuItem["__wasExpanded"]) {
            this.expandWithoutTransition(true);
            delete this.menuItem["__wasExpanded"];
        }
    }

    private addChildrenFromMenuItem() {
        for (const childItem of this.menuItem?.items ?? []) {
            const childNode = new MenuEditorTreeNode({
                menuItem: childItem,
                menuEditor: this.menuEditor
            });
            this.addChild(childNode);
        }
    }

    get isMainMenuNode(): boolean {
        return this.parent == this.tree?.getRootNode();
    }

    get isTopLevelNode(): boolean {
        return this.parent instanceof MenuEditorTreeNode && this.parent.isMainMenuNode;
    }

    protected nodeDoubleClicked(event: DomEvent) {
        this.tree.selectedNode = this;
        this.menuEditor.executeEdit();
        event.stopPropagation();
    }

    private syncUI() {
        const hasChildren = !ArrayUtil.isEmptyArray(this.menuItem.items);

        if (!hasChildren) {
            this.paddingLeft = 20;
            this.paddingRight = 4;
            this.label.setProps({ themeKey: "menu.leafNodeLabel" });
            this._element.style.marginLeft = "";

        } else {
            this.label.height = 32;
        }

        if (this.menuItem?.isSharedMenuReference()) {
            MenuEditorUtil.addSharedMenuIndicator(this.label, this);
        }
    }

    override setLabelProps(props: Partial<LabelProps>) {
        if (props.imageProps && !this.isMainMenuNode) {
            props.imageProps = {
                ...props.imageProps,
                id: "menuEditorTreeNodeImage",
                onClick: this.imageClickListener
            };
        }
        super.setLabelProps(props);
    }

    override set expanded(value: boolean) {
        if (value == this.expanded)
            return;

        if (this.isMainMenuNode && !value) {
            return;
        }

        super.expanded = value;
        if (this.isInitialized) {
            this.label.rotateWithTransition(this.expanded ? 0 : -90, 200);
        }

        // because expandOnClick is false, we need to handle the case where a node containing the selected child is collapsed
        if (!this.expanded && this.tree?.selectedNode != null && !this.tree.selectedNode.isMounted) {
            this.tree.selectedNode = this;
        }
    }

    override get expanded(): boolean {
        return super.expanded;
    }

    expandWithoutTransition(expanded: boolean) {
        super.expanded = expanded;
        this.label.imageRotation = expanded ? 0 : -90;
    }

    public hasChangedData(): boolean {
        return this.menuItem.hasChanged();
    }

    override getPathAsString(separator: string = " > "): string {
        return super.getPathAsStringFromIndex(1, separator);
    }
}
